import React, {useState} from 'react';
import StandardHoursFieldSet, {allDayRange, alwaysClosed, alwaysOpen} from "./StandardHoursFieldSet";
import ExceptionHoursFieldSet from "./ExceptionHoursFieldSet";
import TabButton from 'components/TabButton';
import {Tab} from '@headlessui/react';
import {useFormikContext} from "formik";
import {OpeningHours, StandardHours} from "types/OpeningHoursNew";
import {Info} from "luxon";

// Helpers ----

export {alwaysOpen, alwaysClosed} from "./StandardHoursFieldSet"

enum TabId {INHERIT = 'Inherit', OPEN = 'Always Open', CLOSED = 'Always Closed', CUSTOM = 'Custom'}

function tabPosition(index: number, count: number): number {
  switch (index) {
    case 0:
      return TabButton.positions.FIRST
    case (count - 1):
      return TabButton.positions.LAST
    default:
      return TabButton.positions.MIDDLE
  }
}

function startingTabId(hours?: OpeningHours | null): TabId {
  if(!hours) return TabId.INHERIT

  const dayNames = Info.weekdays().map(day => day.toLowerCase() as keyof StandardHours)

  if(dayNames.every(dayName => hours[dayName][0] === allDayRange)) {
    return TabId.OPEN
  }

  if(dayNames.every(dayName => hours[dayName].length < 1)) {
    return TabId.CLOSED
  }

  return TabId.CUSTOM
}

// Component ----

type OpeningHoursFieldSetProps = {
  name: string
  canInherit?: boolean
}

function OpeningHoursFieldSet({name, canInherit}: OpeningHoursFieldSetProps) {
  const form = useFormikContext()

  const allTabs = [TabId.INHERIT, TabId.OPEN, TabId.CLOSED, TabId.CUSTOM]
  const enabledTabs = allTabs.filter(tabId => tabId !== TabId.INHERIT || !!canInherit)

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(
    enabledTabs.indexOf(startingTabId((form.values as any)[name])) ?? 0
  )

  function handleTabSelect(index: number): void {
    switch(enabledTabs[index]) {
      case TabId.INHERIT:
        void form.setFieldValue(name, null)
        break

      case TabId.OPEN:
        void form.setFieldValue(name, {...alwaysOpen})
        break

      case TabId.CLOSED:
        void form.setFieldValue(name, {...alwaysClosed})
        break

      case TabId.CUSTOM:
        void form.setFieldValue(name, {...alwaysClosed})
        break
    }

    setSelectedTabIndex(index)
  }

  return (
    <fieldset>

      {/* Shortcuts*/}
      <div className="mb-6">
        <Tab.Group selectedIndex={selectedTabIndex} onChange={handleTabSelect}>
          <Tab.List>
            {enabledTabs.map((tabId, index, tabList) => (
              <TabButton position={tabPosition(index, tabList.length)} key={index}>
                {tabId}
              </TabButton>
            ))}
          </Tab.List>
        </Tab.Group>
      </div>

      {enabledTabs[selectedTabIndex] === TabId.CUSTOM &&
        <div className="grid lg:grid-cols-2">

          {/* Standard */}
          <StandardHoursFieldSet name={name}/>

          {/* Exceptions */}
          <ExceptionHoursFieldSet name={name}/>
        </div>
      }
    </fieldset>
  );
}

export default OpeningHoursFieldSet;